/* eslint-disable react/prop-types */
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@egaranti/components";

import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Check, ChevronDown, Loader2, Plus } from "lucide-react";

// EPS-4899 — Kategori seçimi hem "var olandan seç" hem "yeni ekle"yi destekler.
// PROD'daki eski serbest-metin davranışı (CategoryInput) yeniden tasarımda (EPS-4850/
// 4867) sadece-seç dropdown'a dönüşünce yeni kategori ekleme kayboldu. Bu combobox
// ikisini birleştirir: listede olmayan bir ad yazılırsa "«ad» ekle" satırı çıkar ve
// değer `new::<ad>` olarak işaretlenir. BE (crateCategoryIfNotExists) gelen adlardan
// kategoriyi zaten otomatik oluşturuyor — yeni ad göndermek yeterli.
export const NEW_PREFIX = "new::";

export const isNewValue = (v) =>
  typeof v === "string" && v.startsWith(NEW_PREFIX);

export const newValueName = (v) =>
  isNewValue(v) ? v.slice(NEW_PREFIX.length) : "";

const CategoryCombobox = ({
  items = [],
  value = "",
  onChange,
  placeholder = "",
  disabled = false,
  loading = false,
  allowCreate = true,
  ariaLabel,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const selectedLabel = useMemo(() => {
    if (!value) return "";
    if (isNewValue(value)) {
      return `${newValueName(value)} ${t("productsPage.newBadge", "(yeni)")}`;
    }
    const found = items.find((i) => String(i.id) === String(value));
    return found ? found.name : "";
  }, [value, items, t]);

  const trimmed = search.trim();
  const query = trimmed.toLowerCase();

  const filtered = useMemo(() => {
    if (!query) return items;
    return items.filter((i) => i.name?.toLowerCase().includes(query));
  }, [query, items]);

  // Tam (case-insensitive) eşleşme varsa "yeni ekle" gösterme — duplicate olmasın.
  const exactExists = useMemo(
    () => items.some((i) => i.name?.trim().toLowerCase() === query),
    [query, items],
  );

  const showCreateRow = allowCreate && trimmed.length > 0 && !exactExists;

  const close = () => {
    setOpen(false);
    setSearch("");
  };

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setSearch("");
      }}
    >
      <PopoverTrigger asChild>
        {/* Native SelectTrigger ile birebir aynı sınıflar — yükseklik/padding/
            ikon tutarlı olsun (EPS-4899 görsel düzeltme). */}
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-label={ariaLabel || placeholder}
          disabled={disabled}
          className="ring-offset-background focus:ring-ring flex w-full items-center justify-between rounded-lg border border-[#CDD5E0] p-3 text-[#111729] focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1"
        >
          <span className={selectedLabel ? "" : "text-[#677389]"}>
            {selectedLabel || placeholder}
          </span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[--radix-popover-trigger-width] min-w-[280px] p-0"
      >
        <Command shouldFilter={false} className="overflow-visible">
          <CommandInput
            value={search}
            onValueChange={setSearch}
            placeholder={placeholder}
          />
          {loading ? (
            <div className="flex items-center gap-2 px-3 py-3 text-sm text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t("common.loading", "Yükleniyor...")}
            </div>
          ) : (
            <>
              {filtered.length === 0 && !showCreateRow && (
                <CommandEmpty>
                  {allowCreate
                    ? t(
                        "productsPage.typeToCreate",
                        "Eklemek için bir ad yazın.",
                      )
                    : t("common.noResults", "Sonuç bulunamadı")}
                </CommandEmpty>
              )}
              <CommandGroup className="max-h-60 overflow-auto">
                {filtered.map((item) => (
                  <CommandItem
                    key={item.id}
                    value={String(item.id)}
                    onSelect={() => {
                      onChange?.(String(item.id));
                      close();
                    }}
                  >
                    <Check
                      className={`mr-2 h-4 w-4 ${
                        String(value) === String(item.id)
                          ? "opacity-100"
                          : "opacity-0"
                      }`}
                    />
                    {item.name}
                  </CommandItem>
                ))}
                {showCreateRow && (
                  <CommandItem
                    key="__add_new__"
                    value={`__add__${trimmed}`}
                    onSelect={() => {
                      onChange?.(`${NEW_PREFIX}${trimmed}`);
                      close();
                    }}
                    className="text-[#0049E6]"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    {t("productsPage.addNamed", '"{{name}}" ekle', {
                      name: trimmed,
                    })}
                  </CommandItem>
                )}
              </CommandGroup>
            </>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default CategoryCombobox;
